import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Lock,
  FileText,
  DollarSign,
  Calendar,
  User,
  Loader2,
  CheckCircle2,
  Paperclip,
  Link2,
  Tag,
} from 'lucide-react';
import HistoricalTimelineCompare from './HistoricalTimelineCompare';

const estadoBadge = (estado) => {
  const base = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider';
  switch (estado) {
    case 'CERRADO': return { cls: `${base} bg-emerald-100 text-emerald-800 border border-emerald-300`, dot: 'bg-emerald-500' };
    case 'EN_VIGILANCIA': return { cls: `${base} bg-cyan-100 text-cyan-800 border border-cyan-300`, dot: 'bg-cyan-500' };
    case 'ABIERTO': return { cls: `${base} bg-blue-100 text-blue-800 border border-blue-300`, dot: 'bg-blue-500' };
    case 'ARCHIVADO': return { cls: `${base} bg-slate-100 text-slate-700 border border-slate-300`, dot: 'bg-slate-400' };
    case 'EN_INVESTIGACION': return { cls: `${base} bg-amber-100 text-amber-800 border border-amber-300`, dot: 'bg-amber-500' };
    default: return { cls: `${base} bg-slate-100 text-slate-700 border border-slate-300`, dot: 'bg-slate-400' };
  }
};

const scoreColor = (s) => (s >= 80 ? 'text-red-600' : s >= 50 ? 'text-amber-600' : 'text-slate-700');

const fmtMoneyShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
};

export default function PreviousCasesBanner({ casos, caseId, onIncorporated, contextoIncorporado = [], titulo }) {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [incorporatingId, setIncorporatingId] = useState(null);

  // Contexto histórico global (1 fetch para enriquecer todos los items)
  const [histContext, setHistContext] = useState(null);
  // Overlaps por case anterior (fetch on-expand)
  const [overlapCache, setOverlapCache] = useState({});

  useEffect(() => {
    let cancel = false;
    if (!caseId) return;
    (async () => {
      try {
        const res = await api.get(`/cases/${caseId}/historical-context`);
        if (!cancel) setHistContext(res.data);
      } catch {
        if (!cancel) setHistContext(null);
      }
    })();
    return () => { cancel = true; };
  }, [caseId]);

  const enrichedById = useMemo(() => {
    const map = {};
    (histContext?.casos_anteriores || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [histContext]);

  if (!casos || casos.length === 0) return null;

  const normalized = casos
    .map((ca) => (typeof ca === 'string' ? { id: ca } : ca))
    .filter((ca) => ca && ca.id);

  if (normalized.length === 0) return null;

  const toggle = async (ca) => {
    const id = ca.id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!detailCache[id]) {
      setLoadingId(id);
      try {
        const res = await api.get(`/cases/${id}`);
        setDetailCache((prev) => ({ ...prev, [id]: res.data }));
      } catch {
        setDetailCache((prev) => ({ ...prev, [id]: { error: 'No se pudo cargar' } }));
      } finally {
        setLoadingId(null);
      }
    }
    // Fetch overlap in parallel
    if (!overlapCache[id] && caseId) {
      try {
        const ovr = await api.get(`/cases/${caseId}/movimientos-overlap/${id}`);
        setOverlapCache((prev) => ({ ...prev, [id]: ovr.data }));
      } catch {
        setOverlapCache((prev) => ({ ...prev, [id]: { error: true } }));
      }
    }
  };

  const handleIncorporate = async (previousId) => {
    if (!caseId) return;
    setIncorporatingId(previousId);
    try {
      await api.post(`/cases/${caseId}/incorporate-previous`, { previous_case_id: previousId });
      toast.success('Contexto incorporado al caso actual');
      if (onIncorporated) onIncorporated(previousId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al incorporar contexto');
    } finally {
      setIncorporatingId(null);
    }
  };

  const formatCurrency = (n) =>
    Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

  return (
    <div className="p-3 mb-4 bg-orange-50 border border-orange-300 rounded-lg" data-testid="reincidencia-banner">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={18} className="text-orange-600" />
        <span className="text-sm font-bold text-orange-800">
          {titulo || `Cliente reincidente — ${normalized.length} caso${normalized.length > 1 ? 's' : ''} anterior${normalized.length > 1 ? 'es' : ''}`}
        </span>
      </div>

      {/* Timeline comparativo */}
      {caseId && <HistoricalTimelineCompare caseId={caseId} preloaded={histContext} />}

      <div className="space-y-2">
        {normalized.map((ca) => {
          const isExpanded = expandedId === ca.id;
          const detail = detailCache[ca.id];
          const isLoading = loadingId === ca.id;
          const overlap = overlapCache[ca.id];
          const enriched = enrichedById[ca.id] || {};
          const alertasCount = enriched.alertas_count ?? ca.alertas ?? 0;
          const monto = enriched.monto_total ?? 0;
          const tipos = enriched.tipos_alerta || [];
          const estado = ca.estado || enriched.estado || '-';
          const score = ca.score ?? enriched.score_riesgo ?? 0;
          const fecha = ca.fecha || enriched.created_at;
          const eb = estadoBadge(estado);
          const shortId = typeof ca.id === 'string' ? ca.id.slice(-8) : '';
          const isIncorporated = contextoIncorporado.includes(ca.id);
          const commonsCount = (histContext?.comunes?.cuentas_comunes || []).length;

          const alertasReal = detail?.alertas || [];
          const comments = detail?.comments || detail?.comentarios || [];
          const conclusion = comments.find((c) => c.es_conclusion || c.conclusion);
          const montoDetalle = alertasReal.reduce((acc, a) => acc + (Number(a.monto_total || a.monto || 0)), 0);
          const ultComment = comments.length > 0 ? comments[comments.length - 1] : null;

          return (
            <div
              key={ca.id}
              className={`bg-white rounded-lg border ${isExpanded ? 'border-orange-400 shadow-sm' : 'border-orange-200 hover:border-orange-300 hover:shadow-sm'} overflow-hidden transition-all`}
              data-testid={`reincidencia-item-${ca.id}`}
            >
              <button
                type="button"
                onClick={() => toggle(ca)}
                className="w-full flex items-start justify-between gap-3 text-left px-3 py-2.5 hover:bg-orange-50/70 transition-colors"
                data-testid={`reincidencia-toggle-${ca.id}`}
              >
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-100 text-orange-700 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                    <ChevronRight size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    {/* Title row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-800 truncate" title={ca.titulo}>
                        {ca.titulo ? (ca.titulo.length > 40 ? `…${ca.titulo.slice(-36)}` : ca.titulo) : `Caso …${shortId}`}
                      </span>
                      {isIncorporated && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium" title="Contexto incorporado a la investigación actual">
                          <CheckCircle2 size={10} />
                          Incorporado
                        </span>
                      )}
                    </div>
                    {/* Chips row */}
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className={eb.cls}>
                        <span className={`w-1.5 h-1.5 rounded-full ${eb.dot}`} />
                        {estado}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                        <span className="text-slate-400">Score</span>
                        <strong className={scoreColor(score)}>{score}</strong>
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-600" title="Cantidad real de alertas">
                        <AlertTriangle size={10} className="text-orange-500" />
                        <strong className="text-slate-800">{alertasCount}</strong>
                        <span className="text-slate-400">alerta{alertasCount !== 1 ? 's' : ''}</span>
                      </span>
                      {monto > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                          <DollarSign size={10} className="text-emerald-600" />
                          <strong className="text-slate-800">{fmtMoneyShort(monto)}</strong>
                        </span>
                      )}
                      {tipos.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600" title={tipos.join(' · ')}>
                          <Tag size={10} className="text-purple-500" />
                          <strong className="text-slate-800">{tipos.length}</strong>
                          <span className="text-slate-400">tipo{tipos.length !== 1 ? 's' : ''}</span>
                        </span>
                      )}
                      {ca.bloqueada && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-300 font-medium">
                          <Lock size={10} />
                          Bloqueada
                        </span>
                      )}
                      {commonsCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-700" title="Cuentas en común con el caso actual">
                          <Link2 size={10} />
                          {commonsCount} común{commonsCount !== 1 ? 'es' : ''}
                        </span>
                      )}
                      {fecha && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 ml-auto">
                          <Calendar size={10} />
                          {new Date(fecha).toLocaleDateString('es-AR')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 py-3 border-t border-orange-200 bg-orange-50/40 text-xs" data-testid={`reincidencia-expand-${ca.id}`}>
                  {isLoading ? (
                    <div className="flex items-center gap-2 text-slate-500 py-2">
                      <Loader2 size={14} className="animate-spin" />
                      Cargando detalles del caso...
                    </div>
                  ) : detail?.error ? (
                    <div className="text-red-600">{detail.error}</div>
                  ) : detail ? (
                    <div className="space-y-3">
                      {/* Overlap chip */}
                      {overlap && !overlap.error && overlap.compartidos?.count > 0 && (
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                          <Link2 size={14} className="text-amber-700 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-semibold text-amber-900 text-[12px]">
                              {overlap.compartidos.count} movimiento{overlap.compartidos.count !== 1 ? 's' : ''} compartido{overlap.compartidos.count !== 1 ? 's' : ''} con el caso actual
                              <span className="ml-2 text-amber-700 font-normal">({overlap.pct_solape_en_actual}% del actual · {formatCurrency(overlap.compartidos.monto_total)})</span>
                            </div>
                            <div className="text-[11px] text-amber-700/80 mt-0.5">
                              Estos movimientos están dentro del período analizado por ambos casos. Al incluir histórico se deduplican por default.
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Métricas */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="bg-white rounded px-2 py-1.5 border border-orange-100">
                          <div className="text-slate-500 text-[10px] uppercase flex items-center gap-1"><FileText size={10} />Alertas</div>
                          <div className="text-slate-800 font-bold">{alertasReal.length || alertasCount}</div>
                        </div>
                        <div className="bg-white rounded px-2 py-1.5 border border-orange-100">
                          <div className="text-slate-500 text-[10px] uppercase flex items-center gap-1"><DollarSign size={10} />Monto total</div>
                          <div className="text-slate-800 font-bold">{formatCurrency(montoDetalle || monto)}</div>
                        </div>
                        <div className="bg-white rounded px-2 py-1.5 border border-orange-100">
                          <div className="text-slate-500 text-[10px] uppercase flex items-center gap-1"><User size={10} />Responsable</div>
                          <div className="text-slate-800 font-bold truncate">{detail.responsable_nombre || detail.asignado_a || '—'}</div>
                        </div>
                        <div className="bg-white rounded px-2 py-1.5 border border-orange-100">
                          <div className="text-slate-500 text-[10px] uppercase flex items-center gap-1"><Calendar size={10} />Actualizado</div>
                          <div className="text-slate-800 font-bold">{detail.fecha_actualizacion ? new Date(detail.fecha_actualizacion).toLocaleDateString('es-AR') : (detail.updated_at ? new Date(detail.updated_at).toLocaleDateString('es-AR') : '—')}</div>
                        </div>
                      </div>

                      {/* Conclusión */}
                      {conclusion && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                          <div className="flex items-center gap-1 text-emerald-700 font-semibold mb-1">
                            <CheckCircle2 size={12} />
                            Conclusión del caso
                          </div>
                          <div className="text-slate-700 whitespace-pre-wrap">{conclusion.texto || conclusion.comentario}</div>
                        </div>
                      )}

                      {/* Alertas destacadas */}
                      {alertasReal.length > 0 && (
                        <div>
                          <div className="text-slate-500 text-[10px] uppercase font-semibold mb-1">Alertas destacadas</div>
                          <div className="space-y-1">
                            {alertasReal.slice(0, 3).map((a, idx) => (
                              <div key={idx} className="bg-white rounded px-2 py-1 border border-orange-100 flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="font-medium text-slate-700 truncate">{a.tipo || a.regla || 'Alerta'}</div>
                                  <div className="text-slate-500 text-[11px] truncate">{a.descripcion || a.detalle || ''}</div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="font-semibold text-slate-800">{formatCurrency(a.monto_total || a.monto || 0)}</div>
                                  {a.severidad && <div className="text-[10px] text-slate-500">{a.severidad}</div>}
                                </div>
                              </div>
                            ))}
                            {alertasReal.length > 3 && (
                              <div className="text-[11px] text-slate-500 italic">+ {alertasReal.length - 3} alerta(s) más</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Último comentario */}
                      {ultComment && !conclusion && (
                        <div className="bg-white rounded px-3 py-2 border border-orange-100">
                          <div className="text-slate-500 text-[10px] uppercase font-semibold mb-1">Último comentario</div>
                          <div className="text-slate-700 whitespace-pre-wrap line-clamp-3">{ultComment.texto || ultComment.comentario}</div>
                        </div>
                      )}

                      {/* Acciones */}
                      <div className="flex items-center gap-2 pt-1 flex-wrap">
                        <button
                          onClick={() => navigate(`/m/gestion-de-casos/caso/${ca.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded text-xs font-medium transition-colors"
                          data-testid={`reincidencia-open-${ca.id}`}
                        >
                          <ExternalLink size={12} />
                          Abrir caso completo
                        </button>
                        {caseId && (
                          contextoIncorporado.includes(ca.id) ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded text-xs font-medium" data-testid={`reincidencia-already-${ca.id}`}>
                              <CheckCircle2 size={12} />
                              Incorporado a la investigación
                            </span>
                          ) : (
                            <button
                              onClick={() => handleIncorporate(ca.id)}
                              disabled={incorporatingId === ca.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded text-xs font-medium transition-colors"
                              data-testid={`reincidencia-incorporate-${ca.id}`}
                            >
                              {incorporatingId === ca.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                              Incorporar a investigación actual
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
